import moment from 'moment-timezone';
import _ from 'lodash';
import i18n from '~/utils/i18n';
import { currentUser } from '~/store/getters';
import { canGlobal } from '~/utils/permissions';
import { FILTER_TYPES, STATUSES } from '~/utils/constants';
import { availableFilters as genericFilters, toFilterFromToDate, visibleFields } from './generic.js';

export const endpoint = 'campaigns/search';
export const allFields = {
  customer_remote_id: {
    sortable: true,
    formatter: (value, key, data) => _.get(data, 'customer.remote_id', null),
    translateColumn: () => i18n.t('campaign_field.title.customer-remote-id'),
  },
  customer: {
    sortable: true,
    formatter: (value, key, data) => _.get(data, 'customer.name', null),
    translateColumn: () => i18n.t('campaign_field.title.customer-name'),
  },
  creator: {
    sortable: true,
    formatter: (value, key, data) => _.get(data, 'creator_email', null),
    translateColumn: () => i18n.t('campaign_field.title.creator'),
  },
  client_language: {
    sortable: true,
    formatter: (value, key, data) => {
      const val = _.get(data, 'customer.language', null);
      return i18n.t(`${'terms.language.name'}.${val}`);
    },
    translateColumn: () => i18n.t('campaign_field.title.client_language'),
  },
  code: {
    sortable: true,
    translateColumn: () => i18n.t('campaign_field.title.code'),
  },
  status: {
    sortable: true,
    formatter: (value, key, data) => {
      let res = _.get(value, 'name', null);

      if (value.code === STATUSES.ONL && _.get(data, 'issues', []).filter(i => !i.finished_at).length) {
        res = i18n.t('fn.complex.status', { code: `${value.code}-issues` });
      }

      return res;
    },
    translateColumn: () => i18n.t('campaign_field.title.status'),
    translateCell: (data) => {
      data.name = i18n.t('fn.complex.status', { code: data.code });
      return data;
    },
  },
  budget_client: {
    sortable: true,
    formatter: (value, key, data) => i18n.t('fn.currency', { value: _.get(data, 'campaign_rule.budget_client', null) }),
    translateColumn: () => i18n.t('campaign_rule.budget_client'),
  },
  goal_type: {
    newKey: true,
    newKeyFrom: 'campaign_rule.goal_type',
    translateColumn: () => i18n.t('campaign_field.title.goal_type'),
    translateCell: item => i18n.t('fn.complex.goal_type', { goal_type: item }),
  },
  confirmed_at: {
    sortable: true,
    translateColumn: () => i18n.t('campaign_field.title.confirmed-at'),
    translateCell: (data) => {
      if (data !== null && moment(data).isValid()) {
        return i18n.t('fn.date', { timestamp: data });
      }
      return null;
    },
  },
  status_start_date: {
    sortable: false,
    translateColumn: () => i18n.t('campaign_field.title.status-start-date'),
    translateCell: (data) => {
      if (data !== null && moment(data).isValid()) {
        return i18n.t('fn.date', { timestamp: data });
      }
      return null;
    },
  },
  days_in_current_status: {
    sortable: true,
    translateColumn: () => i18n.t('campaign_field.title.days-in-current-status'),
  },
  working_days_in_current_status: {
    sortable: false,
    translateColumn: () => i18n.t('campaign_field.title.working-days-in-current-status'),
  },
  start_date: {
    sortable: true,
    translateColumn: () => i18n.t('campaign_field.title.start-date'),
    translateCell: (data) => {
      if (data !== null && moment(data).isValid()) {
        return i18n.t('fn.date', { timestamp: data });
      }
      return null;
    },
  },
  createdAt: {
    formatter: (value, key, data) => {
      const created = _.get(data, 'created_at', null);
      if (created !== null && moment(created).isValid()) {
        return i18n.t('fn.date', { timestamp: created });
      }
      return null;
    },
    sortable: true,
    translateColumn: () => i18n.t('campaign_field.title.created'),
  },
  name: {
    sortable: true,
    translateColumn: () => i18n.t('campaign_field.title.name'),
  },

  customer_id: {
    sortable: true,
    translateColumn: () => i18n.t('campaign_field.title.customer-id'),
  },

  mode: {
    sortable: true,
    translateColumn: () => i18n.t('campaign_field.title.mode'),
    translateCell: item => i18n.t('fn.complex.mode', { mode: item }),
  },
  locations: {
    key: 'target',
    formatter: (target) => target.locations
      .map(l => i18n.t('campaign_field.format.location', { name: l.name, type: l.type }))
      .join(', '),
    translateColumn: () => i18n.t('campaign_field.title.locations'),
    sortable: false,
  },
  guaranteed_reach: {
    key: 'campaign_rule',
    formatter: (value) => i18n.t('campaign_field.format.guaranteed-reach', { reach: _.get(value, 'min_reach', null) }),
    sortable: false,
    translateColumn: () => i18n.t('campaign_field.title.guaranteed-reach'),
  },
  active_creative_id: {
    sortable: true,
    translateColumn: () => i18n.t('campaign_field.title.active-creative-id'),
  },
  status_flow: {
    sortable: true,
    translateColumn: () => i18n.t('campaign_field.title.status-flow'),
    translateCell: data => i18n.t('fn.complex.campaign-flow-name', { name: data.tag }),
  },
  period_count: {
    sortable: true,
    translateColumn: () => i18n.t('campaign_field.title.period-count'),
  },
  format: {
    key: 'type',
    sortable: true,
    formatter: value => i18n.t('fn.complex.format', { format: value }),
    translateColumn: () => i18n.t('campaign_field.title.format'),
  },
  autopublish: {
    sortable: true,
    formatter: (value) => i18n.t('fn.complex.enabled-disabled', { value }),
    translateColumn: () => i18n.t('campaign_field.title.autopublish'),
  },
  target_change_status: {
    sortable: false,
    formatter: (value) => {
      if (value) {
        return i18n.t(`terms.target_change_status.${value}`);
      }
      return '';
    },
    translateColumn: () => i18n.t('campaign_field.title.target_change_status'),
  },
  is_upsell: {
    sortable: true,
    formatter: (value) => {
      if (value) {
        return i18n.t('pos.search-table.yes');
      }
      return i18n.t('pos.search-table.no');
    },
    translateColumn: () => i18n.t('campaign_field.title.upsells'),
  },
  issues: {
    sortable: false,
    formatter: (value, key, data) => _.get(data, 'issues', [])
        .filter(i => !i.finished_at)
        .map(i => i.name)
        .sort()
        .map(i => i18n.t(`terms.issues.${i}.title`))
        .join(', '),
    translateColumn: () => i18n.t('campaign_field.title.issues'),
  },
  facebook_pixel_last_fired_at: {
    sortable: false,
    formatter: (value, key, data) => {
      const time = _.get(data, 'facebook_pixel.last_fired_at', null);
      return time ? i18n.t('fn.datetime', { timestamp: time }) : i18n.t('pos.pixel.never');
    },
    translateColumn: () => i18n.t('campaign_field.title.facebook_pixel_last_fired_at'),
  },
};

export function availableFields(disabledFields) {
  return visibleFields(disabledFields, allFields);
}

export const enabledFields = [
  'customer_remote_id',
  'customer',
  'code',
  'status',
  'goal_type',
  'confirmed_at',
  'status_start_date',
  'days_in_current_status',
  'working_days_in_current_status',
];

export const simpleEnabledFields = [
  'customer_remote_id',
  'customer',
  'code',
  'status',
  'goal_type',
  'confirmed_at',
  'status_start_date',
  'days_in_current_status',
  'working_days_in_current_status',
];

export const enabledFieldsMD = [
  'start_date',
  'customer',
  'code',
  'status',
  'goal_type',
];

export const enabledFieldsSM = [
  'start_date',
  'customer',
  'status',
  'goal_type',
];

// Format (required fields for all filters)
// [
//   {
//     name: 'Start date', // Filter name visible to user
//     slug: 'startDate', // Filter slug which is sent to ADAPI
//     type: FILTER_DATE_FROM_TO, // Filter type
//     default: { from: null, to: null }, // Filter default value (used when resetting data)
//     getData: null, // Endpoint to get filter values from API
//     formatGetData: null, // Function to parse getData() response from API
//     toObject: value => value, // Function to query paramater to object
//     toFilter: value => value, // Function to parse object to filter request
//   },
// ]
export const availableFilters = [
  {
    name: 'start-date',
    slug: 'startDate',
    type: FILTER_TYPES.DATE_FROM_TO,
    default: { from: null, to: null },
    getData: null,
    formatGetData: null,
    toObject: value => value,
    toFilter: toFilterFromToDate,
    translateFilter: () => i18n.t('campaign_field.title.start-date'),
  },
  {
    name: 'status-flow',
    slug: 'statusFlows',
    type: FILTER_TYPES.MULTISELECT,
    default: null,
    getData: 'resources/status-flows',
    formatGetData: value => value,
    toObject: (value, data) => data.filter(sf => value.indexOf(sf.tag) >= 0),
    toFilter: value => value.map(s => s.tag),
    options: {
      label: 'name',
      trackBy: 'tag',
      groupValues: null,
      groupLabel: null,
      multiple: true,
    },
    translateItemLabel: item => i18n.t('fn.complex.campaign-flow-name', { name: item.tag }),
    translateFilter: () => i18n.t('campaign_field.title.status-flow'),
  },
  {
    name: 'status',
    slug: 'statuses',
    type: FILTER_TYPES.MULTISELECT,
    default: null,
    getData: 'resources/statuses',
    formatGetData: statusFlows => {
      const canViewAllStatuses = canGlobal(currentUser(), 'Campaign', ['viewAllStatuses']);
      const canValidateCampaign = canGlobal(currentUser(), 'Campaign', ['validateCampaigns']);

      let newStatuses = [];

      _.forOwn(statusFlows, statuses => {
        statuses.forEach(s => {
          if (canViewAllStatuses || (s.code !== STATUSES.INI && s.code !== STATUSES.CAN) ||
             (canValidateCampaign && s.code === STATUSES.INI)) {
            newStatuses.push(s);
          }
        });
      });

      newStatuses = _.uniqBy(newStatuses, status => status.code);

      const onlStatus = newStatuses.find(s => s.code === STATUSES.ONL);
      if (onlStatus) {
        const onlWithIssues = _.cloneDeep(onlStatus);
        newStatuses.splice(
          newStatuses.indexOf(onlStatus) + 1,
          0,
          {
            ...onlWithIssues,
            code: 'ONL-issues',
            name: 'ONL (with issues)',
          },
        );
      }

      return _.orderBy(newStatuses, 'stage_order');
    },
    toObject: (value, data) => data.filter(s => value.indexOf(s.code) >= 0),
    toFilter: value => value.map(s => s.code),
    translateItemLabel: item => i18n.t('fn.complex.status', { code: item.code }),
    translateFilter: () => i18n.t('campaign_field.title.status'),
    options: {
      label: 'name',
      trackBy: 'code',
      groupValues: null,
      groupLabel: null,
      multiple: true,
    },
  },
  {
    name: 'issues',
    slug: 'issues',
    type: FILTER_TYPES.MULTISELECT,
    default: null,
    getData: null,
    filterData: [
      { name: 'suspended', value: 'suspended' },
      { name: 'lost_page', value: 'lost_page' },
      { name: 'lost_post', value: 'lost_post' },
      { name: 'disapproved', value: 'disapproved' },
    ],
    formatGetData: res => {
      const seeDisapprovedIssue = canGlobal(currentUser(), 'Campaign', ['seeDisapprovedIssue']);

      if (!seeDisapprovedIssue) {
        return res.filter(i => i.value !== 'disapproved');
      }

      return res;
    },
    toObject: (value, data) => data.filter(c => value.indexOf(c.value) >= 0),
    toFilter: value => value.map(c => c.value),
    options: {
      label: 'name',
      trackBy: 'value',
      groupValues: null,
      groupLabel: null,
      multiple: true,
    },
    translateFilter: () => i18n.t('campaign_field.title.issues'),
    translateItemLabel: item => i18n.t(`terms.issues.${item.name}.title`),
  },
  {
    name: 'package',
    slug: 'packages',
    type: FILTER_TYPES.MULTISELECT,
    default: null,
    getData: 'resources/packages',
    formatGetData: result => result,
    toObject: (value, data) => data.filter(p => value.indexOf(p.package) >= 0),
    toFilter: value => value.map(p => p.package),
    translateFilter: () => i18n.t('campaign_field.title.package'),
    options: {
      label: 'package',
      trackBy: 'package',
      groupValues: null,
      groupLabel: null,
      multiple: true,
    },
  },
  {
    name: 'mode',
    slug: 'modes',
    type: FILTER_TYPES.MULTISELECT,
    default: null,
    getData: 'resources/clusters',
    formatGetData: result => _.uniq(result.map(c => c.mode)).map(c => ({
      name: c,
      value: c,
    })),
    toObject: (value, data) => data.filter(c => value.indexOf(c.value) >= 0),
    toFilter: value => value.map(c => c.value),
    translateItemLabel: item => i18n.t('fn.complex.mode', { mode: item.value }),
    translateFilter: () => i18n.t('campaign_field.title.mode'),
    options: {
      label: 'name',
      trackBy: 'value',
      groupValues: null,
      groupLabel: null,
      multiple: true,
    },
  },
  {
    name: 'days-in-current-status',
    slug: 'daysInCurrentStatus',
    type: FILTER_TYPES.TEXT_INPUT,
    default: null,
    getData: null,
    formatGetData: null,
    toObject: value => value,
    toFilter: value => value,
    translateFilter: () => i18n.t('campaign_field.title.days-in-current-status'),
  },
  {
    name: 'autopublish',
    slug: 'autopublish',
    type: FILTER_TYPES.MULTISELECT,
    default: null,
    getData: 'resources/autopublish',
    formatGetData: result => result.map(c => ({
      name: c,
      value: c,
    })),
    toObject: (value, data) => data.filter(c => value.indexOf(c.value) >= 0),
    toFilter: value => value.map(c => c.value),
    translateItemLabel: item => i18n.t('fn.complex.enabled-disabled', { value: item.value }),
    translateFilter: () => i18n.t('campaign_field.title.autopublish'),
    options: {
      label: 'name',
      trackBy: 'value',
      groupValues: null,
      groupLabel: null,
      multiple: true,
    },
  },
  {
    name: 'goal-type',
    slug: 'goalType',
    type: FILTER_TYPES.MULTISELECT,
    default: null,
    getData: 'resources/goal-types',
    formatGetData: result => result.map(c => ({
      name: c,
      value: c,
    })),
    toObject: (value, data) => data.filter(c => value.indexOf(c.value) >= 0),
    toFilter: value => value.map(c => c.value),
    options: {
      label: 'name',
      trackBy: 'value',
      groupValues: null,
      groupLabel: null,
      multiple: true,
    },
    translateItemLabel: item => i18n.t('fn.complex.goal_type', { goal_type: item.value }),
    translateFilter: () => i18n.t('campaign_field.title.goal_type'),
  },
  {
    slug: 'clientLanguage',
    name: 'client_language',
    type: FILTER_TYPES.MULTISELECT,
    default: null,
    getData: 'config/public',
    formatGetData: result => result.interface_languages.map(c => ({
      name: c,
      value: c,
    })),
    toObject: (value, data) => data.filter(c => value.indexOf(c.value) >= 0),
    toFilter: value => value.map(c => c.value),
    translateFilter: () => i18n.t('campaign_field.title.client_language'),
    translateItemLabel: (item) => i18n.t(`${'terms.language.name'}.${item.name}`),
    options: {
      label: 'name',
      trackBy: 'value',
      groupValues: null,
      groupLabel: null,
      multiple: true,
    },
  },
  {
    slug: 'format',
    type: FILTER_TYPES.MULTISELECT,
    default: null,
    getData: 'creatives/formats',
    formatGetData: result => result.map(c => ({
      name: c,
      value: c,
    })),
    toObject: (value, data) => data.filter(c => value.indexOf(c.value) >= 0),
    toFilter: value => value.map(c => c.value),
    translateFilter: () => i18n.t('campaign_field.title.format'),
    translateItemLabel: item => i18n.t('fn.complex.format', { format: item.name }),
    options: {
      label: 'name',
      trackBy: 'value',
      groupValues: null,
      groupLabel: null,
      multiple: true,
    },
  },
  {
    name: 'ad_status',
    slug: 'adStatus',
    type: FILTER_TYPES.MULTISELECT,
    default: null,
    getData: 'resources/creative-flows',
    formatGetData: flows => {
      const statuses = [];
      const existingSlugs = [];
      flows.forEach(flow => {
        flow.statuses.forEach(s => {
          if (existingSlugs.indexOf(s) < 0) {
            existingSlugs.push(s);
            statuses.push({
              name: i18n.t('fn.complex.creative-flow-statuses-title', { slug: s }),
              slug: s,
            });
          }
        });
      });
      return statuses.length > 0 ? statuses : [];
    },
    toObject: (value, data) => data.filter(c => value.indexOf(c.slug) >= 0),
    toFilter: value => value.map(c => c.slug),
    translateItemLabel: item => i18n.t('fn.complex.creative-flow-statuses-title', { slug: item.slug }),
    translateFilter: () => i18n.t('campaign_field.title.ad-status'),
    options: {
      label: 'name',
      trackBy: 'slug',
      groupValues: null,
      groupLabel: null,
      multiple: true,
    },
  },
  {
    name: 'number_of_reworks',
    slug: 'numberOfReworks',
    type: FILTER_TYPES.TEXT_INPUT,
    default: null,
    getData: null,
    formatGetData: null,
    toObject: value => value,
    toFilter: value => value,
    translateFilter: () => i18n.t('creative_field.title.number_of_reworks'),
  },
  {
    name: 'campaign_creator',
    slug: 'campaignCreator',
    type: FILTER_TYPES.AUTOCOMPLETE,
    template: {
      props: { item: { required: true } },
      template: `
          <div class="text">
            <span class="name">{{ item.email }}</span>
          </div>
      `,
    },
    getLabel(item) {
      return _.get(item, 'email', null);
    },
    clearSearch(slug, internalFilters) {
      internalFilters[slug] = null;
    },
    requestPath: 'autocomplete/creator',
    formatItems: i => i,
    getData: null,
    formatGetData: null,
    toObject: value => value,
    toFilter: value => value,
    translateFilter: () => i18n.t('campaign_field.title.creator'),
  },
  {
    name: 'is_upsell',
    slug: 'isUpsell',
    type: FILTER_TYPES.MULTISELECT,
    default: null,
    getData: null,
    formatGetData: null,
    filterData: [
      { name: 'yes', value: 1 },
      { name: 'no', value: 0 },
    ],
    toObject: (value, data) => data.filter(c => value.indexOf(c.value) >= 0),
    toFilter: value => value.map(c => c.value),
    options: {
      label: 'name',
      trackBy: 'value',
      groupValues: null,
      groupLabel: null,
      multiple: true,
    },
    translateFilter: () => i18n.t('campaign_field.title.upsells'),
    translateItemLabel: item => i18n.t(`pos.search-table.${item.name}`),
  },
  ...genericFilters,
];



// WEBPACK FOOTER //
// ./src/gotu-vue-common/src/search-models/campaign.js